import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BridgeClubLayout from '../../../components/BridgeClub/BridgeClubLayout';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';
import { useBridgeClubAuth } from '../../../contexts/BridgeClubAuthContext';
import './FarmChallengeHub.css';

const FarmChallengeJoin: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useBridgeClubAuth();
  const navigate = useNavigate();
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Redirect if not authenticated
    if (!authLoading && !isAuthenticated) {
      navigate('/bridge-club/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const handleEnroll = async () => {
    try {
      setEnrolling(true);
      setError(null);
      await bridgeClubAPI.enrollInFarmChallenge();
      // Navigate to the hub after successful enrollment
      navigate('/bridge-club/farm-challenge', { replace: true });
    } catch (err: any) {
      console.error('Failed to enroll:', err);
      setError('Failed to enroll. Please try again.');
      setEnrolling(false);
    }
  };

  if (authLoading) {
    return (
      <BridgeClubLayout>
        <div className="farm-challenge-loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </BridgeClubLayout>
    );
  }

  return (
    <BridgeClubLayout>
      <div className="farm-challenge-join">
        <div className="join-header">
          <span className="join-icon">🌾</span>
          <h1>Join the Farm Challenge!</h1>
        </div>

        <div className="join-benefits">
          <p className="join-tagline">Earn points by tracking your farm activities!</p>

          <div className="benefit-list">
            <div className="benefit-item">
              <span className="benefit-icon">🥚</span>
              <div>
                <strong>Log Daily Eggs</strong>
                <p>Track your egg collection and earn 5 points per day</p>
              </div>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">🥛</span>
              <div>
                <strong>Log Milk Production</strong>
                <p>Track dairy yields for 5 points per day</p>
              </div>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">⚖️</span>
              <div>
                <strong>Track Animal Weights</strong>
                <p>Log weights weekly for 10 points per animal</p>
              </div>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">🔪</span>
              <div>
                <strong>Record Harvests</strong>
                <p>Earn 50 points when you process animals</p>
              </div>
            </div>
          </div>

          <div className="points-info">
            <div className="points-conversion">
              <span className="points-value">100 pts</span>
              <span className="points-equals">=</span>
              <span className="points-dollar">$1.00</span>
            </div>
            <p>Redeem points for feed credits at participating stores!</p>
          </div>

          <div className="streak-info">
            <h3>Build Streaks for Bonus Points!</h3>
            <div className="streak-tiers">
              <div className="streak-tier">
                <span className="streak-weeks">7 days</span>
                <span className="streak-bonus">2x points for a week</span>
              </div>
              <div className="streak-tier">
                <span className="streak-weeks">14 days</span>
                <span className="streak-bonus">3x points for a week</span>
              </div>
              <div className="streak-tier highlight">
                <span className="streak-weeks">21 days</span>
                <span className="streak-bonus">4x points for a week!</span>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="join-error">
            <p>{error}</p>
          </div>
        )}

        <button
          className="join-btn"
          onClick={handleEnroll}
          disabled={enrolling}
        >
          {enrolling ? 'Joining...' : 'Start Earning Points'}
        </button>
      </div>
    </BridgeClubLayout>
  );
};

export default FarmChallengeJoin;
